import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Grid2 as Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DragIndicator as DragIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  School as SchoolIcon,
  SmartToy as AIIcon,
  CreditCard as FintechIcon,
  Event as EventIcon,
  Favorite as DonationIcon,
  Bolt as ServiceIcon,
  AutoAwesome as NewIcon,
  Lightbulb as TipIcon,
  LocalOffer as PromoIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface LoginMessage {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  category: string;
  icon?: string;
  bgColor: string;
  textColor: string;
  linkUrl?: string;
  linkText?: string;
  startDate?: string;
  endDate?: string;
  order: number;
  status: string;
  createdAt: string;
}

interface Category {
  value: string;
  label: string;
  icon: string;
  color: string;
}

const categoryIcons: Record<string, React.ReactNode> = {
  LITERACIA: <SchoolIcon />,
  IA: <AIIcon />,
  FINTECH: <FintechIcon />,
  EVENTOS: <EventIcon />,
  DOACOES: <DonationIcon />,
  SERVICOS: <ServiceIcon />,
  NOVIDADES: <NewIcon />,
  DICAS: <TipIcon />,
  PROMOCAO: <PromoIcon />,
  SEGURANCA: <SecurityIcon />,
};

const LoginMessages: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [messages, setMessages] = useState<LoginMessage[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<LoginMessage | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    content: '',
    category: 'NOVIDADES',
    icon: '',
    bgColor: '#ffffff',
    textColor: '#1f2937',
    linkUrl: '',
    linkText: '',
    startDate: '',
    endDate: '',
    order: 0,
    status: 'A',
  });

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/login-messages');
      setMessages(response.data.data || []);
      setNeedsSetup(response.data.needsSetup || false);
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      setError(err.response?.data?.error || t('cms.errorLoadingMessages'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/admin/login-messages/categories');
      setCategories(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    fetchCategories();
  }, [fetchMessages, fetchCategories]);

  const handleSetup = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/admin/login-messages/setup');
      setSuccess(t('cms.tableCreated'));
      setNeedsSetup(false);
      fetchMessages();
    } catch (err: any) {
      setError(err.response?.data?.error || t('cms.errorSettingUpTable'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDialog = (message?: LoginMessage) => {
    if (message) {
      setSelectedMessage(message);
      setFormData({
        title: message.title || '',
        subtitle: message.subtitle || '',
        content: message.content || '',
        category: message.category || 'NOVIDADES',
        icon: message.icon || '',
        bgColor: message.bgColor || '#ffffff',
        textColor: message.textColor || '#1f2937',
        linkUrl: message.linkUrl || '',
        linkText: message.linkText || '',
        startDate: message.startDate ? message.startDate.split('T')[0] : '',
        endDate: message.endDate ? message.endDate.split('T')[0] : '',
        order: message.order || 0,
        status: message.status || 'A',
      });
    } else {
      setSelectedMessage(null);
      setFormData({
        title: '',
        subtitle: '',
        content: '',
        category: 'NOVIDADES',
        icon: '',
        bgColor: '#ffffff',
        textColor: '#1f2937',
        linkUrl: '',
        linkText: '',
        startDate: '',
        endDate: '',
        order: messages.length,
        status: 'A',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMessage(null);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.content) {
      setError(t('cms.titleContentRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (selectedMessage) {
        await api.put(`/admin/login-messages/${selectedMessage.id}`, formData);
        setSuccess(t('cms.messageUpdated'));
      } else {
        await api.post('/admin/login-messages', formData);
        setSuccess(t('cms.messageCreated'));
      }
      handleCloseDialog();
      fetchMessages();
    } catch (err: any) {
      setError(err.response?.data?.error || t('cms.errorSavingMessage'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (message: LoginMessage) => {
    try {
      await api.put(`/admin/login-messages/${message.id}/toggle`);
      fetchMessages();
    } catch (err: any) {
      setError(err.response?.data?.error || t('cms.errorTogglingStatus'));
    }
  };

  const handleDelete = async () => {
    if (!selectedMessage) return;

    setSaving(true);
    try {
      await api.delete(`/admin/login-messages/${selectedMessage.id}`);
      setSuccess(t('cms.messageDeleted'));
      setDeleteDialogOpen(false);
      setSelectedMessage(null);
      fetchMessages();
    } catch (err: any) {
      setError(err.response?.data?.error || t('cms.errorDeletingMessage'));
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryChange = (category: string) => {
    const cat = categories.find(c => c.value === category);
    if (cat) {
      setFormData(prev => ({
        ...prev,
        category,
        icon: cat.icon,
        bgColor: `${cat.color}15`,
        textColor: cat.color,
      }));
    } else {
      setFormData(prev => ({ ...prev, category }));
    }
  };

  const getCategoryLabel = (value: string) => {
    const cat = categories.find(c => c.value === value);
    return cat?.label || value;
  };

  const getCategoryColor = (value: string) => {
    const cat = categories.find(c => c.value === value);
    return cat?.color || '#6B7280';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.loginMessagesTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.loginMessagesSubtitle')}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchMessages}
            sx={{ mr: 1 }}
          >
            {t('common.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={needsSetup}
          >
            {t('cms.newMessage')}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Setup Card */}
      {needsSetup && (
        <Card sx={{ mb: 3, bgcolor: 'warning.light' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6">{t('cms.setupRequired')}</Typography>
                <Typography variant="body2">
                  {t('cms.setupDescription')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={handleSetup}
                disabled={saving}
              >
                {saving ? t('cms.configuring') : t('cms.setupTable')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Preview Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('cms.preview')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 2,
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'action.hover',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'action.disabled',
                borderRadius: 4,
              },
            }}
          >
            {messages.filter(m => m.status === 'A').map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  minWidth: 280,
                  maxWidth: 280,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: msg.bgColor || '#f3f4f6',
                  color: msg.textColor || '#1f2937',
                  flexShrink: 0,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography fontSize={20}>{msg.icon}</Typography>
                  <Chip
                    label={getCategoryLabel(msg.category)}
                    size="small"
                    sx={{
                      bgcolor: `${getCategoryColor(msg.category)}20`,
                      color: getCategoryColor(msg.category),
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {msg.title}
                </Typography>
                {msg.subtitle && (
                  <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                    {msg.subtitle}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.5 }}>
                  {msg.content.length > 100 ? `${msg.content.substring(0, 100)}...` : msg.content}
                </Typography>
                {msg.linkUrl && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1,
                      display: 'block',
                      color: msg.textColor,
                      fontWeight: 'bold',
                    }}
                  >
                    {msg.linkText || t('cms.learnMore')} →
                  </Typography>
                )}
              </Box>
            ))}
            {messages.filter(m => m.status === 'A').length === 0 && (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center', width: '100%' }}>
                {t('cms.noActiveMessages')}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Messages Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell>{t('cms.titleColumn')}</TableCell>
                <TableCell>{t('common.category')}</TableCell>
                <TableCell>{t('cms.period')}</TableCell>
                <TableCell align="center">{t('common.status')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('cms.noMessageFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((msg, index) => (
                  <TableRow key={msg.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DragIcon sx={{ color: 'text.disabled', cursor: 'grab' }} />
                        {index + 1}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize={18}>{msg.icon}</Typography>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {msg.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {msg.content.length > 50 ? `${msg.content.substring(0, 50)}...` : msg.content}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={categoryIcons[msg.category] as React.ReactElement}
                        label={getCategoryLabel(msg.category)}
                        size="small"
                        sx={{
                          bgcolor: `${getCategoryColor(msg.category)}15`,
                          color: getCategoryColor(msg.category),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {msg.startDate || msg.endDate ? (
                        <Typography variant="caption">
                          {msg.startDate ? new Date(msg.startDate).toLocaleDateString('pt-CV', { timeZone: 'UTC' }) : '∞'}
                          {' - '}
                          {msg.endDate ? new Date(msg.endDate).toLocaleDateString('pt-CV', { timeZone: 'UTC' }) : '∞'}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t('cms.always')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={msg.status === 'A' ? t('common.active') : t('common.inactive')}
                        color={msg.status === 'A' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={msg.status === 'A' ? t('cms.deactivate') : t('cms.activate')}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleStatus(msg)}
                        >
                          {msg.status === 'A' ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(msg)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedMessage(msg);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedMessage ? t('cms.editMessage') : t('cms.newMessage')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('cms.titleLabel')}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('cms.subtitleLabel')}
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                placeholder={t('cms.subtitlePlaceholder')}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('cms.content')}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('common.category')}</InputLabel>
                <Select
                  value={formData.category}
                  label={t('common.category')}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{cat.icon}</span>
                        {cat.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.iconEmoji')}
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder={t('cms.iconEmojiPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="color"
                label={t('cms.bgColor')}
                value={formData.bgColor}
                onChange={(e) => setFormData({ ...formData, bgColor: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="color"
                label={t('cms.textColor')}
                value={formData.textColor}
                onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label={t('cms.linkUrlOptional')}
                value={formData.linkUrl}
                onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                placeholder={t('cms.linkUrlPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('cms.linkText')}
                value={formData.linkText}
                onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
                placeholder={t('cms.linkTextPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label={t('cms.startDate')}
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText={t('cms.emptyForAlwaysVisible')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label={t('cms.endDate')}
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText={t('cms.emptyForAlwaysVisible')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('cms.order')}
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 'A'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'A' : 'I' })}
                  />
                }
                label={t('cms.messageActive')}
              />
            </Grid>
          </Grid>

          {/* Preview */}
          <Box mt={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('cms.cardPreview')}
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: formData.bgColor || '#f3f4f6',
                color: formData.textColor || '#1f2937',
                maxWidth: 300,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography fontSize={20}>{formData.icon}</Typography>
                <Chip
                  label={getCategoryLabel(formData.category)}
                  size="small"
                  sx={{
                    bgcolor: `${getCategoryColor(formData.category)}20`,
                    color: getCategoryColor(formData.category),
                    fontSize: '0.7rem',
                  }}
                />
              </Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {formData.title || t('cms.titlePlaceholder')}
              </Typography>
              {formData.subtitle && (
                <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                  {formData.subtitle}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.5 }}>
                {formData.content || t('cms.contentPlaceholder')}
              </Typography>
              {formData.linkUrl && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: 'block',
                    fontWeight: 'bold',
                  }}
                >
                  {formData.linkText || t('cms.learnMore')} →
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('cms.saving') : selectedMessage ? t('cms.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('cms.deleteMessage')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeleteMessage', { name: selectedMessage?.title })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? t('cms.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginMessages;
